import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Search, X } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import { usePageMeta } from "@/lib/usePageMeta";
import { track } from "@/lib/analytics";
import { PUBLISHED_TOOLS, TOOL_CATEGORIES, TOOL_TYPES, categoryOf } from "@/data/tools/registry";
import { GRID } from "@/components/tools/ui";

const FEATURED_SLUGS = ["growth-system-scorecard", "discount-damage-calculator", "ecommerce-profitability-calculator", "sales-productivity-calculator"];

// The Commercial Growth Leak Map — navigational visual: where growth leaks,
// each zone links to the tool category that fixes it.
const LEAK_ZONES = [
  { cat: "growth-strategy", label: "Strategy & Focus", desc: "No clear choice on where growth comes from" },
  { cat: "pricing-profitability", label: "Pricing & Margin", desc: "Discounts, fees and schemes outgrow the price" },
  { cat: "gtm-distribution", label: "Distribution", desc: "Buyers can't find or get the product" },
  { cat: "sales-excellence", label: "Sales Engine", desc: "Pipeline leaks between stages" },
  { cat: "brand-marketing", label: "Demand & Brand", desc: "Activity without profitable demand" },
  { cat: "digital-commerce", label: "Digital Economics", desc: "Revenue that survives fees and returns" },
  { cat: "market-intelligence", label: "Customer Insight", desc: "Decisions made on stale assumptions" },
  { cat: "industry", label: "Industry Plays", desc: "Sector-specific economics and diagnostics" },
];

function ToolCard({ t, compact }) {
  const cat = categoryOf(t.category);
  return (
    <Link
      to={t.url}
      data-testid={`tool-card-${t.id}`}
      onClick={() => track("tool_card_click", { tool: t.id })}
      className="group border border-zinc-200 bg-white p-5 flex flex-col hover:border-black transition-colors duration-300"
    >
      <div className="flex items-center justify-between">
        <span className="overline">{t.id}</span>
        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">{TOOL_TYPES[t.type]}</span>
      </div>
      <div className="mt-3 font-serif-display text-lg leading-snug group-hover:text-zinc-700">{t.name}</div>
      {!compact && <p className="mt-2 text-[13px] text-zinc-600 leading-relaxed flex-1">{t.hook}</p>}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-zinc-400">{cat.name}</span>
        <ArrowUpRight className="h-4 w-4 text-zinc-300 group-hover:text-black group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
      </div>
    </Link>
  );
}

export default function ToolsHubPage() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [type, setType] = useState("all");
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: "Free Business Growth Tools & Calculators | BLEZZINGS",
    description: "Free business growth tools that turn questions into decisions — pricing, distribution, sales, e-commerce and brand calculators, diagnostics and planners for Indian businesses.",
    canonical: "https://blezzings.co/tools",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Growth Tools", url: "https://blezzings.co/tools" },
    ],
  });

  const published = PUBLISHED_TOOLS;
  const featured = published.filter((t) => FEATURED_SLUGS.includes(t.slug));
  const counts = Object.fromEntries(TOOL_CATEGORIES.map((c) => [c.id, published.filter((t) => t.category === c.id).length]));

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return published.filter((t) => {
      if (cat !== "all" && t.category !== cat) return false;
      if (type !== "all" && t.type !== type) return false;
      if (needle && !`${t.name} ${t.hook} ${t.output} ${t.buyer} ${categoryOf(t.category).name}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [q, cat, type, published]);

  return (
    <div data-testid="tools-hub" className="relative">
      <Nav onBook={() => setBookOpen(true)} />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-[#fafafa]">
        <div className={GRID}>
          <SectionBadge label="BLEZZINGS Growth Tools & Resources" />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl">
            Free Business Growth Tools That Turn Questions into Decisions
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Practical intelligence for better business decisions. Diagnose the problem, quantify the opportunity, plan the action — free to use, no sign-up for results.
          </p>

          {/* Search + filters */}
          <div className="mt-10 flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center flex-1 border border-zinc-300 bg-white focus-within:border-black transition-colors">
              <Search className="ml-3 h-4 w-4 text-zinc-400" />
              <input
                type="search"
                data-testid="tools-search"
                placeholder="Search tools — pricing, distributors, ROAS, growth…"
                className="w-full px-3 py-3 text-sm outline-none bg-transparent"
                value={q}
                onChange={(e) => { setQ(e.target.value); track("tool_search", { q: e.target.value.slice(0, 40) }); }}
              />
            </div>
            <div className="relative">
              <select
                data-testid="tools-filter-category"
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                className="h-[46px] w-full md:w-auto pl-3 pr-9 border border-zinc-300 bg-white text-sm outline-none appearance-none focus:border-black transition-colors"
              >
                <option value="all">All problems</option>
                {TOOL_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" aria-hidden />
            </div>
            <div className="relative">
              <select
                data-testid="tools-filter-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="h-[46px] w-full md:w-auto pl-3 pr-9 border border-zinc-300 bg-white text-sm outline-none appearance-none focus:border-black transition-colors"
              >
                <option value="all">All formats</option>
                {Object.entries(TOOL_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" aria-hidden />
            </div>
          </div>

          {/* Filter feedback + reset — results render in the grid below */}
          {(cat !== "all" || type !== "all" || q.trim()) && (
            <div
              className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border border-zinc-300 bg-white px-4 py-3"
              data-testid="tools-filter-active"
            >
              <p className="text-sm text-zinc-700">
                <span className="font-semibold text-black">{filtered.length}</span> of {published.length} tools
                {cat !== "all" && <> · {categoryOf(cat).name}</>}
                {type !== "all" && <> · {TOOL_TYPES[type]}</>}
                {q.trim() && <> · matching “{q.trim()}”</>}
              </p>
              <div className="flex items-center gap-3 ml-auto">
                <a
                  href="#tools-grid"
                  data-testid="tools-filter-view"
                  className="text-sm font-medium text-black underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
                >
                  View results ↓
                </a>
                <button
                  type="button"
                  data-testid="tools-filter-reset"
                  onClick={() => { setQ(""); setCat("all"); setType("all"); }}
                  className="inline-flex items-center gap-1.5 h-9 px-4 bg-black text-white text-xs font-medium hover:bg-zinc-800 transition-colors"
                >
                  <X className="h-3.5 w-3.5" /> Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Commercial Growth Leak Map */}
      <section className="py-12 md:py-14 bg-white" data-testid="leak-map">
        <div className={GRID}>
          <div className="overline">The Commercial Growth Leak Map</div>
          <h2 className="font-serif-display text-2xl md:text-3xl mt-3">Where is growth leaking?</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LEAK_ZONES.map((z) => (
              <button
                key={z.cat}
                type="button"
                data-testid={`leak-${z.cat}`}
                onClick={() => { setCat(z.cat); track("leak_map_click", { zone: z.cat }); document.getElementById("tools-grid")?.scrollIntoView({ behavior: "smooth" }); }}
                className={`text-left border p-5 transition-colors ${cat === z.cat ? "border-black bg-zinc-50" : "border-zinc-200 bg-white hover:border-black"}`}
              >
                <div className="font-serif-display text-lg">{z.label}</div>
                <p className="mt-1.5 text-[13px] text-zinc-600 leading-relaxed">{z.desc}</p>
                <div className="mt-3 text-xs font-medium">{counts[z.cat]} tools →</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && cat === "all" && !q && (
        <section className="py-12 md:py-14 bg-[#fafafa]" data-testid="tools-featured">
          <div className={GRID}>
            <div className="overline">Start here</div>
            <h2 className="font-serif-display text-2xl md:text-3xl mt-3">Featured Growth Tools</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.map((t) => <ToolCard key={t.id} t={t} />)}
            </div>
          </div>
        </section>
      )}

      {/* Full grid by category */}
      <section id="tools-grid" className="py-14 md:py-16 bg-white" data-testid="tools-grid">
        <div className={GRID}>
          {filtered.length === 0 && (
            <p className="text-sm text-zinc-500" data-testid="tools-empty">
              No published tools match that yet — the library is growing weekly. Try a broader search.
            </p>
          )}
          {TOOL_CATEGORIES.filter((c) => cat === "all" || cat === c.id).map((c) => {
            const items = filtered.filter((t) => t.category === c.id);
            if (!items.length) return null;
            return (
              <div key={c.id} className="mb-12" data-testid={`tools-cat-${c.id}`}>
                <div className="flex items-baseline justify-between border-b-2 border-black pb-3">
                  <h2 className="font-serif-display text-2xl md:text-3xl">{c.name}</h2>
                  <span className="overline">{items.length} {items.length === 1 ? "tool" : "tools"}</span>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((t) => <ToolCard key={t.id} t={t} compact />)}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-zinc-400 max-w-2xl leading-relaxed">
            All 104 tools are live — every tool is free to use, works on your real numbers, and shows its method. New tools are announced on LinkedIn.
          </p>
        </div>
      </section>

      <ContextualCTA
        testid="tools-cta"
        heading="Tools find the problem. Engagements fix it."
        body="Every tool here is how BLEZZINGS starts a conversation — bring your output to a free 30-minute discussion and we'll tell you honestly whether it's worth working together."
        label="Book a Growth Discussion"
        onBook={() => setBookOpen(true)}
      />

      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
      <Footer onJoin={() => setJoinOpen(true)} />
    </div>
  );
}
